import { setup } from 'xstate';
import { PageNames } from '../../../../lib/analytics';
import { RouteMachineServiceEvents } from '../../types/events';
import { getRoutePathConditionGuards } from '../../guards/routePathConditions';
import { getRoutePathConditionGuardProps } from '../../actions';
import { getDefaultBuyerErrorRouteGuards } from '../../guards/buyerMachineErrors';
import { getDefaultApplicationErrorRouteGuards } from '../../guards/applicationMachineErrors';
import {
  getRoutingMachineLevelEvents,
  isRouteEqualToStateNode,
} from '../../transitions';

export enum Routes {
  Intro = '/alliance',
  Email = '/alliance/email',
  Mobile = '/alliance/mobile',
  Verification = '/alliance/verification',
  BillingAddress = '/alliance/billing-address',
  Eligibility = '/alliance/eligibility',
  Terms = '/alliance/terms',
  Review = '/alliance/review',
  LoginEligibility = '/alliance/login-eligibility',
  ConfirmEmail = '/alliance/confirm-email',
  NeedsFullIIN = '/alliance/needs-full-iin',
  IDVerificationAlloyNeedsAction = '/alliance/id-verification/alloy-needs-action',
  IDVerificationAlloy = '/alliance/id-verification/alloy',
  // Error Routes
  AgeAlabamaMilitary = '/alliance/errors/age-alabama-military',
  AgeIneligible = '/alliance/errors/age-ineligible',
  BuyerHashFailed = '/alliance/errors/buyer-hash-failed',
  BuyerSkippedInstallments = '/alliance/errors/buyer-skipped-installments',
  BuyerStatusIneligible = '/alliance/errors/buyer-status-ineligible',
  CreditDenial = '/alliance/errors/credit-denial',
  CreditFreeze = '/alliance/errors/credit-freeze',
  IDVerificationFailure = '/alliance/errors/id-verification-failure',
  InvalidAddressNoMatchIneligible = '/alliance/errors/invalid-address-no-match-ineligible',
  KycDenial = '/alliance/errors/kyc-denial',
  LoanStatusIneligible = '/alliance/errors/loan-status-ineligible',
  LocationIneligible = '/alliance/errors/location-ineligible',
  MaxCardIneligible = '/alliance/errors/max-card-ineligible',
  NeedsActionWarning = '/alliance/errors/needs-action-warning',
  NeedsAdditionalInformationWarning = '/alliance/errors/needs-additional-information-warning',
  OFACDenial = '/alliance/errors/ofac-denial',
  SanctionDenial = '/alliance/errors/sanctions-denial',
  OutstandingLoansIneligible = '/alliance/errors/outstanding-loans-ineligible',
  POBoxAddressIneligible = '/alliance/errors/po-box-address-ineligible',
  PreviousDenialIneligible = '/alliance/errors/previous-denial-ineligible',
  VerifiedEmail = '/alliance/errors/verified-email',
  FraudAlertDenial = '/alliance/errors/fraud-alert-denial',
  FraudDenial = '/alliance/errors/fraud-denial',
  CapacityRecheck = '/alliance/errors/capacity-recheck',
  AlloySystemOutage = '/alliance/errors/alloy-system-outage',
  Unknown = '/alliance/errors/unknown-error',
}

export const routingMachine = setup({
  types: {
    events: {} as RouteMachineServiceEvents,
  },
  guards: {
    isBuyerReadyComplete: (): boolean => {
      throw new Error('Not implemented');
    },
    isUnauthenticatedInitial: (): boolean => {
      throw new Error('Not implemented');
    },
    isExchangedNonStreamlined: (): boolean => {
      throw new Error('Not implemented');
    },
    isExchangedStreamlined: (): boolean => {
      throw new Error('Not implemented');
    },
    isExchangedWithPaymentAgreement: (): boolean => {
      throw new Error('Not implemented');
    },
    isRouteEqualToStateNode,
    ...getRoutePathConditionGuards(),
    ...getDefaultBuyerErrorRouteGuards(),
    ...getDefaultApplicationErrorRouteGuards(),
  },
  actions: {
    addLoadingIndicator: (): void => {
      throw new Error('Not implemented');
    },
    removeLoadingIndicator: (): void => {
      throw new Error('Not implemented');
    },
  },
}).createMachine({
  id: 'router',
  initial: 'initializing',
  on: {
    ...getRoutingMachineLevelEvents(Routes),
  },
  states: {
    initializing: {
      on: {
        SEND_RESET_ROUTER_STATE: {
          target: Routes.Intro,
        },
        SEND_ROUTE_APPROVED_COMPLETE_INITIAL_BUYER: [
          {
            guard: {
              type: 'isExchangedNonStreamlined',
            },
            target: Routes.BillingAddress,
          },
          {
            guard: {
              type: 'isExchangedWithPaymentAgreement',
            },
            target: Routes.Review,
          },
          {
            target: Routes.Terms,
          },
        ],
        SEND_ROUTE_EXCHANGED_BUYER: {
          target: Routes.Mobile,
        },
        SEND_ROUTE_APPLICATION_NEEDS_FULL_IIN: [
          {
            guard: {
              type: 'isExchangedStreamlined',
            },
            target: Routes.NeedsFullIIN,
          },
          // TODO - Add correct error page (Design Pending)
          {
            target: Routes.Unknown,
          },
        ],
        SEND_ROUTE_INCOMPLETE_BUYER: [
          {
            guard: {
              type: 'isExchangedStreamlined',
            },
            target: Routes.Eligibility,
          },
          {
            target: Routes.BillingAddress,
          },
        ],
        SEND_ROUTE_INVALID_APPLICATION_COMPLETE_BUYER: {
          target: Routes.BillingAddress,
        },
      },
      always: [
        {
          guard: {
            type: 'isUnauthenticatedInitial',
          },
          target: Routes.Intro,
        },
      ],
      exit: [
        {
          type: 'removeLoadingIndicator',
        },
      ],
    },
    [Routes.Intro]: {
      meta: {
        analyticsPageName: PageNames.Intro,
        prefetchRoutes: [Routes.Email],
      },
      on: {
        SEND_FORWARD: [
          {
            target: Routes.Email,
          },
        ],
      },
    },
    [Routes.Email]: {
      meta: {
        analyticsPageName: PageNames.Email,
        prefetchRoutes: [Routes.Mobile],
      },
      on: {
        SEND_FORWARD: {
          target: Routes.Mobile,
        },
        SEND_BACK: {
          target: Routes.Intro,
        },
      },
    },
    [Routes.Mobile]: {
      meta: {
        analyticsPageName: PageNames.Mobile,
        prefetchRoutes: [Routes.Verification],
      },
      on: {
        SEND_FORWARD: {
          target: Routes.Verification,
        },
        SEND_BACK: [
          {
            target: Routes.Email,
          },
        ],
      },
    },
    [Routes.Verification]: {
      meta: {
        analyticsPageName: PageNames.Verification,
        prefetchRoutes: [Routes.BillingAddress, Routes.Terms],
      },
      on: {
        SEND_BACK: {
          target: Routes.Mobile,
        },
        SEND_ROUTE_NEW_BUYER: {
          target: Routes.BillingAddress,
        },
        SEND_ROUTE_AUTH_RETRY_WITH_PII: [
          {
            target: Routes.LoginEligibility,
          },
        ],
        SEND_ROUTE_APPROVED_COMPLETE_INITIAL_BUYER: [
          {
            guard: {
              type: 'isExchangedNonStreamlined',
            },
            target: Routes.BillingAddress,
          },
          {
            guard: {
              type: 'isExchangedWithPaymentAgreement',
            },
            target: Routes.Review,
          },
          {
            target: Routes.Terms,
          },
        ],
        SEND_ROUTE_COMPLETE_IDENTITY_BUYER: {
          target: Routes.BillingAddress,
        },
        SEND_ROUTE_INCOMPLETE_BUYER: [
          {
            guard: {
              type: 'isExchangedStreamlined',
            },
            target: Routes.Eligibility,
          },
          {
            target: Routes.BillingAddress,
          },
        ],
        SEND_ROUTE_INVALID_APPLICATION_COMPLETE_BUYER: {
          target: Routes.BillingAddress,
        },
      },
    },
    [Routes.BillingAddress]: {
      meta: {
        analyticsPageName: PageNames.BillingAddress,
        prefetchRoutes: [Routes.Terms, Routes.Eligibility],
      },
      on: {
        SEND_ROUTE_INCOMPLETE_BUYER: {
          target: Routes.Eligibility,
        },
        SEND_ROUTE_APPROVED_COMPLETE_CONTACT_UPDATED_BUYER: [
          {
            guard: {
              type: 'isExchangedNonStreamlined',
            },
            target: Routes.Eligibility,
          },
          {
            target: Routes.Terms,
          },
        ],
        SEND_ROUTE_APPROVED_COMPLETE_INITIAL_BUYER: [
          {
            guard: {
              type: 'isExchangedNonStreamlined',
            },
            target: Routes.Eligibility,
          },
          {
            target: Routes.Terms,
          },
        ],
        SEND_ROUTE_APPLICATION_NEEDS_FULL_IIN: [
          {
            guard: {
              type: 'isExchangedStreamlined',
            },
            target: Routes.NeedsFullIIN,
          },
          // TODO - Add correct error page (Design Pending)
          {
            target: Routes.Unknown,
          },
        ],
        SEND_ROUTE_INVALID_APPLICATION_COMPLETE_BUYER: {
          target: Routes.Eligibility,
        },
      },
    },
    [Routes.Eligibility]: {
      meta: {
        analyticsPageName: PageNames.Eligibility,
        prefetchRoutes: [Routes.Terms],
      },
      on: {
        SEND_ROUTE_APPROVED_COMPLETE_DOB_UPDATED_BUYER: {
          target: Routes.Terms,
        },
        SEND_ROUTE_APPLICATION_NEEDS_FULL_IIN: [
          {
            guard: {
              type: 'isExchangedStreamlined',
            },
            target: Routes.NeedsFullIIN,
          },
          // TODO - Add correct error page (Design Pending)
          {
            target: Routes.Unknown,
          },
        ],
        SEND_BACK: {
          target: Routes.BillingAddress,
        },
      },
    },
    [Routes.LoginEligibility]: {
      meta: {
        analyticsPageName: PageNames.LoginEligibility,
        prefetchRoutes: [Routes.Terms, Routes.BillingAddress],
      },
      on: {
        SEND_ROUTE_APPROVED_COMPLETE_INITIAL_BUYER: [
          {
            guard: {
              type: 'isExchangedStreamlined',
            },
            target: Routes.Terms,
          },
          {
            target: Routes.ConfirmEmail,
          },
        ],
        SEND_ROUTE_NEW_BUYER: {
          target: Routes.BillingAddress,
        },
        SEND_ROUTE_INCOMPLETE_BUYER: {
          target: Routes.BillingAddress,
        },
        SEND_ROUTE_COMPLETE_IDENTITY_BUYER: {
          target: Routes.BillingAddress,
        },
        SEND_BACK: {
          target: Routes.Verification,
        },
      },
    },
    [Routes.ConfirmEmail]: {
      meta: {
        analyticsPageName: PageNames.ConfirmEmail,
        prefetchRoutes: [Routes.Terms],
      },
      on: {
        SEND_ROUTE_APPROVED_COMPLETE_EMAIL_UPDATED_BUYER: {
          target: Routes.BillingAddress,
        },
        SEND_ROUTE_APPROVED_COMPLETE_DOB_UPDATED_BUYER: {
          target: Routes.BillingAddress,
        },
      },
    },
    [Routes.NeedsFullIIN]: {
      meta: {
        analyticsPageName: PageNames.NeedsFullIIN,
        prefetchRoutes: [Routes.Terms],
      },
      on: {
        SEND_ROUTE_APPROVED_COMPLETE_INITIAL_BUYER: {
          target: Routes.Terms,
        },
        SEND_ROUTE_APPROVED_COMPLETE_DOB_UPDATED_BUYER: {
          target: Routes.Terms,
        },
      },
    },
    [Routes.Terms]: {
      meta: {
        analyticsPageName: PageNames.Terms,
        prefetchRoutes: [Routes.Review],
      },
      on: {
        SEND_FORWARD: {
          target: Routes.Review,
        },
      },
    },
    [Routes.Review]: {
      meta: {
        analyticsPageName: PageNames.Review,
        prefetchRoutes: [],
      },
      on: {
        SEND_BACK: {
          target: Routes.Terms,
        },
        SEND_ROUTE_APPLICATION_NEEDS_ID_VERIFICATION: {
          target: Routes.IDVerificationAlloyNeedsAction,
        },
        SEND_ROUTE_WITH_CONDITION: [
          {
            guard: {
              type: 'isBillingAddressRouteCondition',
              params: getRoutePathConditionGuardProps,
            },
            target: Routes.BillingAddress,
          },
        ],
        SEND_ROUTE_APPROVED_COMPLETE_INITIAL_BUYER: {
          target: Routes.Terms,
        },
        SEND_ROUTE_APPROVED_COMPLETE_CONTACT_UPDATED_BUYER: {
          target: Routes.Terms,
        },
        SEND_ROUTE_APPROVED_COMPLETE_DOB_UPDATED_BUYER: {
          target: Routes.Terms,
        },
      },
    },
    [Routes.IDVerificationAlloyNeedsAction]: {
      meta: {
        analyticsPageName: PageNames.IDVerificationAlloyNeedsAction,
        prefetchRoutes: [],
      },
      on: {
        SEND_FORWARD: {
          target: Routes.IDVerificationAlloy,
        },
      },
    },
    [Routes.IDVerificationAlloy]: {
      meta: {
        analyticsPageName: PageNames.IDVerificationAlloy,
        prefetchRoutes: [],
      },
      on: {
        SEND_ROUTE_APPROVED_COMPLETE_INITIAL_BUYER: {
          target: Routes.Review,
        },
        SEND_ROUTE_APPROVED_COMPLETE_CONTACT_UPDATED_BUYER: {
          target: Routes.Review,
        },
        SEND_ROUTE_APPROVED_COMPLETE_DOB_UPDATED_BUYER: {
          target: Routes.Review,
        },
      },
    },
    // Error Routes
    [Routes.AgeAlabamaMilitary]: {
      meta: {
        analyticsPageName: PageNames.AgeAlabamaMilitaryError,
        prefetchRoutes: [],
      },
    },
    [Routes.AgeIneligible]: {
      meta: {
        analyticsPageName: PageNames.BuyerAgeIneligibleError,
        prefetchRoutes: [],
      },
    },
    [Routes.BuyerHashFailed]: {
      meta: {
        analyticsPageName: PageNames.BuyerHashError,
        prefetchRoutes: [],
      },
      on: {
        SEND_ROUTE_APPROVED_COMPLETE_INITIAL_BUYER: {
          target: Routes.Terms,
        },
        SEND_ROUTE_APPROVED_COMPLETE_CONTACT_UPDATED_BUYER: {
          target: Routes.Terms,
        },
        SEND_ROUTE_APPROVED_COMPLETE_DOB_UPDATED_BUYER: {
          target: Routes.Terms,
        },
      },
    },
    [Routes.BuyerSkippedInstallments]: {
      meta: {
        analyticsPageName: PageNames.BuyerSkippedInstallmentsError,
        prefetchRoutes: [],
      },
    },
    [Routes.BuyerStatusIneligible]: {
      meta: {
        analyticsPageName: PageNames.BuyerStatusIneligible,
        prefetchRoutes: [],
      },
    },
    [Routes.CreditDenial]: {
      meta: {
        analyticsPageName: PageNames.CreditDenialError,
        prefetchRoutes: [],
      },
    },
    [Routes.CreditFreeze]: {
      meta: {
        analyticsPageName: PageNames.CreditFreezeError,
        prefetchRoutes: [],
      },
    },
    [Routes.IDVerificationFailure]: {
      meta: {
        analyticsPageName: PageNames.IDVerificationFailure,
        prefetchRoutes: [],
      },
    },
    [Routes.KycDenial]: {
      meta: {
        analyticsPageName: PageNames.KYCDenialError,
        prefetchRoutes: [],
      },
    },
    [Routes.LoanStatusIneligible]: {
      meta: {
        analyticsPageName: PageNames.LoanStatusIneligibleError,
        prefetchRoutes: [],
      },
    },
    [Routes.LocationIneligible]: {
      meta: {
        analyticsPageName: PageNames.LocationIneligibleError,
        prefetchRoutes: [],
      },
    },
    [Routes.MaxCardIneligible]: {
      meta: {
        analyticsPageName: PageNames.MaxCardIneligibleError,
        prefetchRoutes: [],
      },
    },
    [Routes.NeedsActionWarning]: {
      meta: {
        analyticsPageName: PageNames.NeedsActionWarningError,
        prefetchRoutes: [],
      },
    },
    [Routes.NeedsAdditionalInformationWarning]: {
      meta: {
        analyticsPageName: PageNames.NeedsAdditionalInformationWarningError,
        prefetchRoutes: [],
      },
    },
    [Routes.OFACDenial]: {
      meta: {
        analyticsPageName: PageNames.OFACError,
        prefetchRoutes: [],
      },
    },
    [Routes.OutstandingLoansIneligible]: {
      meta: {
        analyticsPageName: PageNames.OutstandingLoanIneligibleError,
        prefetchRoutes: [],
      },
    },
    [Routes.POBoxAddressIneligible]: {
      meta: {
        analyticsPageName: PageNames.POBoxAddressIneligibleError,
        prefetchRoutes: [],
      },
      on: {
        SEND_FORWARD: {
          target: Routes.BillingAddress,
        },
      },
    },
    [Routes.InvalidAddressNoMatchIneligible]: {
      meta: {
        analyticsPageName: PageNames.InvalidAddressNoMatchIneligibleError,
        prefetchRoutes: [],
      },
      on: {
        SEND_FORWARD: {
          target: Routes.BillingAddress,
        },
      },
    },
    [Routes.PreviousDenialIneligible]: {
      meta: {
        analyticsPageName: PageNames.PreviousDenialError,
        prefetchRoutes: [],
      },
    },
    [Routes.VerifiedEmail]: {
      meta: {
        analyticsPageName: PageNames.VerifiedEmailError,
        prefetchRoutes: [],
      },
    },
    [Routes.SanctionDenial]: {
      meta: {
        analyticsPageName: PageNames.SanctionDenialError,
        prefetchRoutes: [],
      },
    },
    [Routes.FraudAlertDenial]: {
      meta: {
        analyticsPageName: PageNames.FraudAlertDenialError,
        prefetchRoutes: [],
      },
    },
    [Routes.FraudDenial]: {
      meta: {
        analyticsPageName: PageNames.FraudDenialError,
        prefetchRoutes: [],
      },
    },
    [Routes.CapacityRecheck]: {
      meta: {
        analyticsPageName: PageNames.CapacityRecheckError,
        prefetchRoutes: [],
      },
      on: {
        SEND_ROUTE_APPROVED_COMPLETE_INITIAL_BUYER: {
          target: Routes.Terms,
        },
        SEND_ROUTE_APPROVED_COMPLETE_CONTACT_UPDATED_BUYER: {
          target: Routes.Terms,
        },
        SEND_ROUTE_APPROVED_COMPLETE_DOB_UPDATED_BUYER: {
          target: Routes.Terms,
        },
      },
    },
    [Routes.AlloySystemOutage]: {
      meta: {
        analyticsPageName: PageNames.AlloySystemOutageError,
        prefetchRoutes: [],
      },
    },
    [Routes.Unknown]: {
      meta: {
        analyticsPageName: PageNames.UnknownError,
        prefetchRoutes: [],
      },
    },
  },
});
